import 'jspdf-autotable'
import jsPDF from 'jspdf'
import * as XLSX from 'xlsx'
import {saveAs} from 'file-saver'

declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF
  }
}

interface TableColumn {
  title: string
  key: string
}

// Define AutoTableOptions type
interface AutoTableOptions {
  startY?: number
  head?: (string | string[])[]
  body?: (string | number)[][]
  data?: unknown[] // Array of objects
  fileName?: string
}

export const downloadCSV = (data: any, filename: string) => {
  // Extracting keys from the first object in jsonData
  const keys = Object.keys(data[0])

  // Creating CSV content with headers
  let csvContent = keys.join(',') + '\n'
  csvContent += data.map((row: any) => keys.map((key) => row[key]).join(',')).join('\n')

  // Creating a data URI
  const encodedUri = encodeURI('data:text/csv;charset=utf-8,' + csvContent)

  // Creating a link element to trigger the download
  const link = document.createElement('a')
  link.setAttribute('href', encodedUri)
  link.setAttribute('download', `${filename}.csv`)
  document.body.appendChild(link)
  link.click()
}

export const downloadPDF = async (options: any) => {
  try {
    const {data, fileName, startY} = options

    // Ensure data exists and has at least one item
    if (!data || data.length === 0) {
      throw new Error('Data is empty or undefined')
    }

    // Assert the type of data as an array of objects
    const dataArray = data as object[]
    if (!dataArray[0]) {
      throw new Error('Data is empty')
    }

    // Extract columns dynamically from the first item in the data array
    const columns: TableColumn[] = Object.keys(dataArray[0]).map((key) => ({
      title: key,
      key: key,
    }))

    // Generate PDF
    const doc = new jsPDF()
    doc.text(fileName!, 10, 10)

    const head = columns.map((column) => column.title)
    const body = dataArray.map((item) =>
      columns.map((column) => String((item as never)[column.key]))
    )

    doc.autoTable({
      startY: startY || 20,
      head: [head],
      body: body,
      theme: 'grid', // Change table theme to 'grid'
      styles: {
        fontSize: 10, // Set font size to 10
        cellPadding: 2, // Set cell padding to 2
      },
      columnStyles: {0: {cellWidth: '50px'}}, // Set the width of the first column to 'auto'
      margin: {top: 20}, // Set margin top to 20
    })

    // Download PDF
    doc.save(`${fileName}.pdf`)
  } catch (error) {
    console.error('Error generating PDF:', error)
  }
}

export const convertToTwoWords = (str: string) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const handleSampleDownload = <T extends Record<string, any>>(
  content: T[],
  filename: string
) => {
  const twoWordsDownloadContent = content.map((item: any) => {
    const newItem: Record<string, any> = {}
    for (const key in item) {
      newItem[convertToTwoWords(key)] = item[key]
    }
    return newItem
  })

  const worksheet = XLSX.utils.json_to_sheet(twoWordsDownloadContent)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

  // Buffer to store the generated Excel file
  const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'})
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  })

  saveAs(blob, `${filename}.xlsx`)
}
